/**
 * Note title marker + site block helpers (storefront «Блок 1…5»).
 *
 *   @@ttl@@
 *   Title text
 *   @@/ttl@@
 *
 *   Body markdown…
 */

export const NOTE_SITE_BLOCKS = [
  'none',
  'block1',
  'block2',
  'block3',
  'block4',
  'block5',
] as const;

export type NoteSiteBlock = (typeof NOTE_SITE_BLOCKS)[number];

export const NOTE_BLOG_LAYOUTS = [
  'stack',
  'image_left',
  'image_right',
  'images_row',
  'blog_alternating',
] as const;

export type NoteBlogLayout = (typeof NOTE_BLOG_LAYOUTS)[number];

export const NOTE_IMAGE_FITS = ['square', 'asis'] as const;
export type NoteImageFit = (typeof NOTE_IMAGE_FITS)[number];

export const NOTE_IMAGE_WIDTHS = [25, 50, 75, 100] as const;
export type NoteImageWidth = (typeof NOTE_IMAGE_WIDTHS)[number];

export const NOTE_ALT_GAP_MIN = 0;
export const NOTE_ALT_GAP_MAX = 20;

export const NOTE_ALT_PADDING_MIN = 0;
export const NOTE_ALT_PADDING_MAX = 24;

export const NOTE_ALT_TEXT_INSET_MIN = 0;
export const NOTE_ALT_TEXT_INSET_MAX = 48;

export const NOTE_ALT_TEXT_ALIGNS = ['left', 'center', 'right'] as const;
export type NoteAltTextAlign = (typeof NOTE_ALT_TEXT_ALIGNS)[number];

export const NOTE_ALT_STARTS = ['image_first', 'text_first'] as const;
export type NoteAltStart = (typeof NOTE_ALT_STARTS)[number];

/** Theme / appearance roles for alternating strip colors (stored as ids, resolved live). */
export const NOTE_COLOR_ROLES = [
  'pageBg',
  'headerBg',
  'footerBg',
  'textPrimary',
  'textSecondary',
  'borderColor',
  'accent',
] as const;

export type NoteColorRole = (typeof NOTE_COLOR_ROLES)[number];

const TITLE_BLOCK_RE =
  /^@@ttl@@[ \t]*\r?\n([\s\S]*?)\r?\n@@\/ttl@@[ \t]*(?:\r?\n|$)/;

const MAX_TITLE_LENGTH = 200;
export const NOTE_IMAGE_MAX_COUNT = 6;

export function splitNoteTitleAndBody(raw: string): {
  title: string | null;
  bodyMd: string;
} {
  const text = String(raw || '');
  const match = text.match(TITLE_BLOCK_RE);
  if (!match) {
    return { title: null, bodyMd: text };
  }
  const title = match[1]
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_TITLE_LENGTH);
  const bodyMd = text.slice(match[0].length).replace(/^\s+/, '');
  return {
    title: title || null,
    bodyMd,
  };
}

/** Compose body_md with optional title block. */
export function composeNoteBodyMd(title: string | null | undefined, bodyMd: string): string {
  const body = String(bodyMd || '').trim();
  const cleanTitle = String(title || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_TITLE_LENGTH);
  if (!cleanTitle) return body;
  return `@@ttl@@\n${cleanTitle}\n@@/ttl@@\n\n${body}`;
}

export type NoteBodyMdI18n = { ru: string; en: string; he: string };

export function emptyNoteBodyMdI18n(): NoteBodyMdI18n {
  return { ru: '', en: '', he: '' };
}

export function normalizeNoteBodyMdI18n(raw: unknown, fallbackBodyMd = ''): NoteBodyMdI18n {
  const out = emptyNoteBodyMdI18n();
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  for (const lang of ['ru', 'en', 'he'] as const) {
    out[lang] = typeof src[lang] === 'string' ? src[lang] : '';
  }
  if (!out.ru && !out.en && !out.he && fallbackBodyMd) {
    out.ru = fallbackBodyMd;
    out.en = fallbackBodyMd;
    out.he = fallbackBodyMd;
  }
  return out;
}

/** Canonical body_md mirror for search / legacy readers. */
export function primaryNoteBodyMd(i18n: NoteBodyMdI18n): string {
  return i18n.ru.trim() || i18n.en.trim() || i18n.he.trim() || '';
}

/** True when that locale has any title or body text inside composed markdown. */
export function noteLocaleHasText(i18n: NoteBodyMdI18n | null | undefined, locale: 'ru' | 'en' | 'he'): boolean {
  return Boolean(String(i18n?.[locale] || '').trim());
}

/** Strip markdown links/images hrefs — keep visible text. */
export function stripMarkdownLinks(raw: string): string {
  let text = String(raw || '');
  text = text.replace(/!\[[^\]]*\]\([^)]*\)/g, '');
  text = text.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');
  text = text.replace(/<https?:\/\/[^>]+>/gi, '');
  text = text.replace(/\bhttps?:\/\/[^\s<]+/gi, '');
  return text;
}

export function normalizeNoteBlogLayout(raw: unknown): NoteBlogLayout {
  if ((NOTE_BLOG_LAYOUTS as readonly string[]).includes(String(raw || ''))) {
    return raw as NoteBlogLayout;
  }
  return 'stack';
}

export function normalizeNoteImageFit(raw: unknown): NoteImageFit {
  return raw === 'asis' ? 'asis' : 'square';
}

export function normalizeNoteImageWidth(raw: unknown): NoteImageWidth {
  const n = Number(raw);
  if ((NOTE_IMAGE_WIDTHS as readonly number[]).includes(n)) {
    return n as NoteImageWidth;
  }
  return 50;
}

export function normalizeNoteAltGap(raw: unknown): number {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return 0;
  return Math.min(NOTE_ALT_GAP_MAX, Math.max(NOTE_ALT_GAP_MIN, n));
}

export function normalizeNoteAltPadding(raw: unknown): number {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return 16;
  return Math.min(NOTE_ALT_PADDING_MAX, Math.max(NOTE_ALT_PADDING_MIN, n));
}

export function normalizeNoteAltTextInset(raw: unknown): number {
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n)) return 0;
  return Math.min(NOTE_ALT_TEXT_INSET_MAX, Math.max(NOTE_ALT_TEXT_INSET_MIN, n));
}

export function normalizeNoteAltTextAlign(raw: unknown): NoteAltTextAlign {
  if (raw === 'left' || raw === 'right' || raw === 'center') return raw;
  return 'center';
}

export function normalizeNoteAltStart(raw: unknown): NoteAltStart {
  return raw === 'text_first' ? 'text_first' : 'image_first';
}

export function normalizeNoteColorRole(
  raw: unknown,
  fallback: NoteColorRole,
): NoteColorRole {
  const key = String(raw || '').trim();
  if ((NOTE_COLOR_ROLES as readonly string[]).includes(key)) {
    return key as NoteColorRole;
  }
  return fallback;
}

export function normalizeNoteSiteBlock(raw: unknown): NoteSiteBlock {
  const key = String(raw || '').trim().toLowerCase();
  if ((NOTE_SITE_BLOCKS as readonly string[]).includes(key)) {
    return key as NoteSiteBlock;
  }
  return 'none';
}

/** Storefront module id for a placed block (block1 → block1). */
export function storefrontModuleForSiteBlock(
  block: Exclude<NoteSiteBlock, 'none'>,
): string {
  return block;
}

export function normalizeNoteImageUrls(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const item of raw) {
    if (typeof item !== 'string') continue;
    const url = item.trim();
    if (!url.startsWith('/uploads/')) continue;
    if (!out.includes(url)) out.push(url);
    if (out.length >= NOTE_IMAGE_MAX_COUNT) break;
  }
  return out;
}

/** Blank-line paragraphs for blog_alternating (BIO pattern). */
export function splitNoteParagraphs(text: string, max = NOTE_IMAGE_MAX_COUNT): string[] {
  return String(text || '')
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean)
    .slice(0, max);
}

/** CSS variable for a note color role (storefront shell already sets these). */
export function noteColorRoleCssVar(role: NoteColorRole): string {
  switch (role) {
    case 'pageBg':
      return 'var(--sf-page-bg, var(--bg-primary))';
    case 'headerBg':
      return 'var(--sf-header-bg, var(--bg-secondary))';
    case 'footerBg':
      return 'var(--sf-footer-bg, var(--bg-secondary))';
    case 'textPrimary':
      return 'var(--text-primary)';
    case 'textSecondary':
      return 'var(--text-secondary)';
    case 'borderColor':
      return 'var(--border-color)';
    case 'accent':
      return 'var(--accent-primary)';
    default:
      return 'var(--text-primary)';
  }
}

export type NoteRoleColorMap = Record<NoteColorRole, string>;

/** Resolve roles to hex for admin form swatches. */
export function buildNoteRoleColorMap(
  palette: {
    pageBg: string;
    headerBg: string;
    footerBg: string;
    textPrimary: string;
    textSecondary: string;
    borderColor: string;
  },
  accent: string,
): NoteRoleColorMap {
  return {
    pageBg: palette.pageBg,
    headerBg: palette.headerBg,
    footerBg: palette.footerBg,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    borderColor: palette.borderColor,
    accent: accent || '#2563eb',
  };
}
